import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  FileText,
  Hash,
  CreditCard,
  Paperclip,
  PenLine,
  Store,
  Ticket,
  User,
  UserCheck,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { Endorsement, ENDORSEMENT_STATUS_LABEL, EndorsementStatus } from "@/types/endorsement";
import { Mask } from "@/utils/mask";

type DetailsModalProps = {
  endorsement: Endorsement | null;
  onClose: () => void;
};

const STATUS_BADGE: Record<EndorsementStatus, { className: string; icon: React.ReactNode }> = {
  pending: { className: "bg-yellow-100 text-yellow-800", icon: <Clock className="h-3.5 w-3.5" /> },
  approved: { className: "bg-green-100 text-green-800", icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
  rejected: { className: "bg-red-100 text-red-800", icon: <XCircle className="h-3.5 w-3.5" /> },
};

const formatDateTime = (value: string) => new Date(value).toLocaleString("pt-BR");

const InfoRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <div className="break-words text-sm font-medium text-gray-900 dark:text-zinc-50">{children || "—"}</div>
    </div>
  </div>
);

const StatTile = ({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone: string;
}) => (
  <div className="flex items-center justify-between rounded-lg border bg-white p-3 dark:bg-zinc-900">
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-zinc-800">
      {icon}
    </div>
  </div>
);

export const EndorsementDetailsModal = ({ endorsement, onClose }: DetailsModalProps) => (
  <Dialog open={!!endorsement} onOpenChange={(open) => !open && onClose()}>
    <AppDialogContent
      icon={PenLine}
      title="Detalhes do Endossamento"
      description={endorsement ? `Venda #${endorsement.sale_id}` : undefined}
      className="sm:max-w-2xl"
    >
      {endorsement && (
        <>
          <AppDialogBody className="gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${STATUS_BADGE[endorsement.status].className}`}
              >
                {STATUS_BADGE[endorsement.status].icon}
                {ENDORSEMENT_STATUS_LABEL[endorsement.status]}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                <Paperclip className="h-3.5 w-3.5" />
                {endorsement.files.length} {endorsement.files.length === 1 ? "anexo" : "anexos"}
              </span>
            </div>

            {endorsement.status === "rejected" && endorsement.reviewer_notes && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <div>
                  <p className="font-semibold">Motivo da recusa</p>
                  <p>{endorsement.reviewer_notes}</p>
                </div>
              </div>
            )}

            <section className="grid grid-cols-1 gap-4 rounded-lg border bg-white p-4 sm:grid-cols-2 dark:bg-zinc-900">
              <InfoRow icon={<User className="h-4 w-4" />} label="Nome completo">
                {endorsement.client_name}
              </InfoRow>
              <InfoRow icon={<CreditCard className="h-4 w-4" />} label="CPF">
                {Mask.maskCpfOrCnpj(endorsement.client_document)}
              </InfoRow>
              <InfoRow icon={<Store className="h-4 w-4" />} label="Vendedor">
                {endorsement.seller_name}
              </InfoRow>
              <InfoRow icon={<Hash className="h-4 w-4" />} label="Venda">
                <Link
                  to={`/vendas?sale_id=${endorsement.sale_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  #{endorsement.sale_id}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </InfoRow>
            </section>

            <div className="grid grid-cols-2 gap-3">
              <StatTile
                icon={<Ticket className="h-4 w-4" />}
                label="Qtd. de bilhetes"
                value={endorsement.tickets_quantity}
                tone="text-gray-900 dark:text-zinc-50"
              />
              <StatTile
                icon={<DollarSign className="h-4 w-4" />}
                label="Valor"
                value={Mask.formatCurrency(endorsement.total_value)}
                tone="text-green-700 dark:text-green-400"
              />
            </div>

            <section className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Produtos</p>
              <ul className="divide-y rounded-lg border bg-white dark:bg-zinc-900">
                {endorsement.products.map((product, index) => (
                  <li key={`${product.name}-${index}`} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="shrink-0 rounded px-2 py-0.5 text-xs font-bold uppercase text-white"
                        style={{ backgroundColor: product.lottery_color || "#475569" }}
                      >
                        {product.lottery ?? "—"}
                      </span>
                      <span className="truncate text-sm">
                        {product.name}
                        {product.contest && <span className="text-gray-500"> · Concurso {product.contest}</span>}
                      </span>
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums">{product.quantity}x</span>
                  </li>
                ))}
                {endorsement.products.length === 0 && (
                  <li className="px-3 py-2 text-sm text-muted-foreground">Nenhum produto.</li>
                )}
              </ul>
            </section>

            {endorsement.description && (
              <section className="space-y-2">
                <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <FileText className="h-3.5 w-3.5" />
                  Descrição
                </p>
                <p className="whitespace-pre-wrap rounded-lg border bg-white p-3 text-sm dark:bg-zinc-900">
                  {endorsement.description}
                </p>
              </section>
            )}

            <section className="grid grid-cols-1 gap-4 rounded-lg border bg-gray-50 p-4 sm:grid-cols-2 dark:bg-zinc-900/50">
              <InfoRow icon={<CalendarClock className="h-4 w-4" />} label="Solicitado em">
                {formatDateTime(endorsement.created_at)}
              </InfoRow>
              {endorsement.reviewer_name && (
                <InfoRow icon={<UserCheck className="h-4 w-4" />} label="Avaliado por">
                  {endorsement.reviewer_name}
                  {endorsement.reviewed_at && (
                    <span className="block text-xs font-normal text-gray-500">
                      {formatDateTime(endorsement.reviewed_at)}
                    </span>
                  )}
                </InfoRow>
              )}
            </section>
          </AppDialogBody>

          <AppDialogFooter>
            <Button type="button" className={appDialogSecondaryButton} onClick={onClose}>
              Fechar
            </Button>
          </AppDialogFooter>
        </>
      )}
    </AppDialogContent>
  </Dialog>
);
